import lodash from 'lodash';

import DateString from '$shared/models/DateString';
import BaseModel from '$shared/models/BaseModel';
import ParameterSet from './ParameterSet';
import type { EntityParameterType } from '$shared/constants/EntityParameterType';
import type { IDatasetMeta } from '$shared/interfaces/api/IDatasetMeta';
import type { IParameterSet } from '$shared/interfaces/api/IParameterSet';
import { DatasetLabel } from '$shared/constants/DatasetLabel';
import type { DatasetCapability } from '$shared/constants/DatasetCapability';
import type { DatasetTag } from '$shared/constants/DatasetTag';
import type { ModuleEntityType } from '$shared/constants/ModuleEntityType';
import { MaintenanceDowntimeCronDescription } from './MaintenanceDowntimeCronDescription';

const ProcessingTimeLimit = 20;

export default class DatasetMeta extends BaseModel<IDatasetMeta> {
  declare public name: string;
  declare public type: string;
  declare public tags: DatasetTag[];
  declare public parameterSets: ParameterSet[];
  declare public monitoringIntervals: string[];
  declare public lastUpdated: DateString;
  declare public schema: Record<string, any>;
  declare public properties: Record<string, any>;
  declare public capabilities: Record<string, DatasetCapability>;
  declare public status: ObjectType<any>;
  declare public maintenanceWindows: MaintenanceDowntimeCronDescription[];

  constructor(data: IDatasetMeta) {
    super(data);
  }

  public get hasPassThroughFee(): boolean {
    return !!this.response?.hasPassThroughFee;
  }

  get parsedName(): string {
    return this.customLabel || this.name;
  }

  get customLabel(): string {
    return DatasetLabel[this.type];
  }

  public get defaultParamsetId(): string {
    return this.parameterSets?.find((paramset) => paramset.isPreferred)?.id || '';
  }

  get isDpsSource(): boolean {
    return this.status?.expectedProcessingTime
      ? ProcessingTimeLimit < this.status?.expectedProcessingTime
      : false;
  }

  get allRequiredParameters(): EntityParameterType[] {
    return lodash.union(
      ...this.parameterSets.map((paramSet: ParameterSet) => paramSet.requiredParameterTypes)
    );
  }

  get allSupportedParameters(): EntityParameterType[] {
    return lodash.union(
      ...this.parameterSets.map((paramSet: ParameterSet) => paramSet.supportedParameterTypes)
    );
  }

  get commonParamTypeInRequiredParamters(): EntityParameterType[] {
    return lodash.intersectionWith(
      ...this.parameterSets.map((paramSet: ParameterSet) => paramSet.requiredParameterTypes),
      lodash.isEqual
    );
  }

  public get supportedEntityTypes(): ModuleEntityType[] {
    return this.response?.supportedEntityTypes || [];
  }

  public get pendingUploadedFiles(): { fileName: string; lastModified: string }[] {
    return this.properties?.pendingUploadedFiles || [];
  }

  public get pendingUploadedFileModifiedDate(): DateString | null {
    return DateString.create(this.pendingUploadedFiles?.first()?.lastModified);
  }

  public get isInMaintenance(): boolean {
    return this.maintenanceWindows.some((window) => window.isInMaintenance);
  }

  public getAllSupportedParametersByEntityType(
    entityType: ModuleEntityType
  ): EntityParameterType[] {
    return ParameterSet.getParametersByEntityType(this.parameterSets, entityType).reduce(
      (acc: EntityParameterType[], paramSet: ParameterSet) =>
        lodash.union(acc, paramSet.supportedParameterTypes),
      []
    );
  }

  protected override transformResponse(): void {
    this.name = this.response.name;
    this.type = this.response.type;
    this.tags = this.response.tags;
    this.parameterSets = this.response.parameterSets.map(
      (parameterSet: IParameterSet) => new ParameterSet(parameterSet)
    );
    this.monitoringIntervals = this.response.monitoringIntervals;
    if (this.response.lastUpdated) {
      this.lastUpdated = new DateString(this.response.lastUpdated);
    }
    this.schema = this.response.schema;
    this.properties = this.response.properties;
    this.capabilities = this.response.capabilities;
    this.status = this.response.status;
    this.maintenanceWindows =
      this.response.maintenanceWindows?.map(
        (window) => new MaintenanceDowntimeCronDescription(window)
      ) || [];
  }
}
